import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import UserPage from 'flarum/forum/components/UserPage';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import extractText from 'flarum/common/utils/extractText';
import BadgeCard from './BadgeCard';
import BadgeModal from './BadgeModal';
import AssignBadgeModal from './AssignBadgeModal';
import UserRecalculateModal from './UserRecalculateModal';
export default class UserBadgesPage extends UserPage {
  constructor() {
    super(...arguments);
    _defineProperty(this, "loading", true);
    _defineProperty(this, "loadingMore", false);
    _defineProperty(this, "userBadges", []);
    _defineProperty(this, "totalBadgeCount", 0);
    _defineProperty(this, "hasMoreBadges", false);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.loadUser(m.route.param('username'));
  }
  show(user) {
    super.show(user);
    this.loadUserBadges();
  }
  async loadUserBadges(loadMore) {
    if (loadMore === void 0) {
      loadMore = false;
    }
    if (!this.user) return;
    if (loadMore) {
      this.loadingMore = true;
    } else {
      this.loading = true;
      this.userBadges = [];
    }
    m.redraw();
    try {
      var _response$meta, _response$meta2;
      const offset = loadMore ? this.userBadges.length : 0;
      const response = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/user-badges',
        params: {
          'filter[user]': this.user.id(),
          include: 'badge,badge.category',
          sort: '-earnedAt',
          'page[offset]': offset,
          'page[limit]': 20
        }
      });

      // Store includes in the store first
      if (response.included) {
        response.included.forEach(inc => {
          app.store.pushObject(inc);
        });
      }

      // Parse JSON:API response
      const newBadges = response.data.map(data => app.store.pushObject(data));
      if (loadMore) {
        this.userBadges = [...this.userBadges, ...newBadges];
      } else {
        this.userBadges = newBadges;
      }

      // Get total from meta — backend returns meta.page.total (JSON:API pagination envelope)
      if (((_response$meta = response.meta) == null || (_response$meta = _response$meta.page) == null ? void 0 : _response$meta.total) !== undefined) {
        this.totalBadgeCount = response.meta.page.total;
      } else if (((_response$meta2 = response.meta) == null ? void 0 : _response$meta2.total) !== undefined) {
        // Fallback: flat meta.total format
        this.totalBadgeCount = response.meta.total;
      }

      // Check if there are more badges to load
      this.hasMoreBadges = this.userBadges.length < this.totalBadgeCount;
    } catch (error) {
      console.error('Failed to load user badges:', error);
      if (!loadMore) {
        this.userBadges = [];
        this.totalBadgeCount = 0;
      }
    } finally {
      this.loading = false;
      this.loadingMore = false;
      m.redraw();
    }
  }
  content() {
    if (this.loading) {
      return m("div", {
        className: "UserBadgesPage UserBadgesPage--loading"
      }, m(LoadingIndicator, null));
    }
    const canModerate = app.forum.attribute('canGiveBadges') || app.forum.attribute('canModerateBadges');
    if (this.userBadges.length === 0) {
      return m("div", {
        className: "UserBadgesPage"
      }, canModerate && this.user && m("div", {
        className: "UserBadgesPage-header"
      }, m("h2", null, app.translator.trans('fof-badges.forum.user.badges_count', {
        count: 0
      })), this.renderRecalculateButton()), m("div", {
        className: "UserBadgesPage-emptyState"
      }, m("i", {
        className: "fas fa-award UserBadgesPage-emptyIcon"
      }), m("p", null, app.translator.trans('fof-badges.forum.user.no_badges'))));
    }
    return m("div", {
      className: "UserBadgesPage"
    }, m("div", {
      className: "UserBadgesPage-header"
    }, m("h2", null, app.translator.trans('fof-badges.forum.user.badges_count', {
      count: this.totalBadgeCount
    })), canModerate && this.user && this.renderRecalculateButton()), m("div", {
      className: "UserBadgesPage-grid"
    }, this.userBadges.map(ub => {
      const badge = ub.badge();
      if (!badge) return null;
      const showNewTag = this.isRecentBadge(ub.earnedAt()) && app.forum.attribute('badgeNewHighlightEnabled');
      const isOwner = app.session.user && this.user && app.session.user.id() === this.user.id();
      const canRevoke = canModerate && ub.grantedBy() === 'manual';
      const showActions = isOwner || canRevoke;
      return m("div", {
        className: "UserBadgesPage-item",
        key: ub.id()
      }, m("div", {
        className: "UserBadgesPage-badgeWrapper"
      }, m(BadgeCard, {
        badge: badge,
        onclick: () => this.showBadgeModal(badge),
        earnedAt: ub.earnedAt(),
        isFavorite: ub.isPrimary(),
        isHidden: !ub.showOnCard(),
        isNew: showNewTag,
        isManual: ub.grantedBy() === 'manual',
        isOwner: isOwner,
        reason: ub.reason()
      })), showActions && m(Dropdown, {
        className: "UserBadgesPage-actions",
        buttonClassName: "Button Button--icon Button--flat",
        icon: "fas fa-ellipsis-v",
        accessibleToggleLabel: app.translator.trans('fof-badges.forum.user.badge_actions')
      }, isOwner && m(Button, {
        className: "hasIcon",
        icon: ub.isPrimary() ? 'fas fa-star' : 'far fa-star',
        onclick: () => this.toggleFavorite(ub)
      }, ub.isPrimary() ? app.translator.trans('fof-badges.forum.user.remove_favorite') : app.translator.trans('fof-badges.forum.user.set_favorite')), isOwner && !ub.isPrimary() && m(Button, {
        className: "hasIcon",
        icon: ub.showOnCard() ? 'fas fa-eye-slash' : 'fas fa-eye',
        onclick: () => this.toggleVisibility(ub)
      }, ub.showOnCard() ? app.translator.trans('fof-badges.forum.user.hide_badge') : app.translator.trans('fof-badges.forum.user.show_badge')), canRevoke && m(Button, {
        className: "hasIcon",
        icon: "fas fa-trash",
        onclick: () => this.revokeBadge(ub, badge.name())
      }, app.translator.trans('fof-badges.forum.user.revoke_badge'))));
    })), this.hasMoreBadges && m("div", {
      className: "UserBadgesPage-loadMore"
    }, m(Button, {
      className: "Button",
      loading: this.loadingMore,
      onclick: () => this.loadUserBadges(true)
    }, app.translator.trans('fof-badges.forum.user.load_more'))));
  }
  renderRecalculateButton() {
    return m("div", {
      className: "UserBadgesPage-moderatorActions"
    }, m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-plus",
      onclick: () => this.showAssignModal()
    }, app.translator.trans('fof-badges.forum.user.assign_badge')), m(Button, {
      className: "Button",
      icon: "fas fa-sync-alt",
      onclick: () => this.showRecalculateModal()
    }, app.translator.trans('fof-badges.forum.user.recalculate')));
  }
  showAssignModal() {
    if (!this.user) return;
    app.modal.show(AssignBadgeModal, {
      user: this.user,
      onAssign: () => {
        this.loadUserBadges();
      }
    });
  }
  showRecalculateModal() {
    if (!this.user) return;
    app.modal.show(UserRecalculateModal, {
      user: this.user,
      onRecalculate: () => {
        this.loadUserBadges();
      }
    });
  }
  showBadgeModal(badge) {
    app.modal.show(BadgeModal, {
      badge
    });
  }

  /**
   * Check if badge was earned within the last 7 days
   */
  isRecentBadge(earnedAt) {
    if (!earnedAt) return false;
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return new Date(earnedAt) > sevenDaysAgo;
  }
  async toggleFavorite(userBadge) {
    try {
      const response = await app.request({
        method: 'POST',
        url: "".concat(app.forum.attribute('apiUrl'), "/user-badges/").concat(userBadge.id(), "/toggle"),
        body: {
          action: 'toggleFavorite'
        }
      });
      if (response.success) {
        // Update local state - if this badge is now primary, unset others
        if (response.is_primary) {
          this.userBadges.forEach(ub => {
            if (ub.id() !== userBadge.id()) {
              ub.pushAttributes({
                isPrimary: false
              });
            }
          });
        }
        userBadge.pushAttributes({
          isPrimary: response.is_primary,
          showOnCard: response.show_on_card
        });
        m.redraw();
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  }
  async toggleVisibility(userBadge) {
    try {
      const response = await app.request({
        method: 'POST',
        url: "".concat(app.forum.attribute('apiUrl'), "/user-badges/").concat(userBadge.id(), "/toggle"),
        body: {
          action: 'toggleVisibility'
        }
      });
      if (response.success) {
        userBadge.pushAttributes({
          showOnCard: response.show_on_card
        });
        m.redraw();
      }
    } catch (error) {
      console.error('Failed to toggle visibility:', error);
    }
  }
  async revokeBadge(userBadge, badgeName) {
    if (!confirm(extractText(app.translator.trans('fof-badges.forum.user.revoke_confirm', {
      badge: badgeName
    })))) {
      return;
    }
    try {
      await app.request({
        method: 'DELETE',
        url: "".concat(app.forum.attribute('apiUrl'), "/user-badges/").concat(userBadge.id())
      });

      // Remove from local list
      this.userBadges = this.userBadges.filter(ub => ub.id() !== userBadge.id());
      app.alerts.show({
        type: 'success'
      }, app.translator.trans('fof-badges.forum.user.badge_revoked', {
        badge: badgeName
      }));
      m.redraw();
    } catch (error) {
      console.error('Failed to revoke badge:', error);
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('fof-badges.forum.user.revoke_error'));
    }
  }
}
flarum.reg.add('fof-badges', 'forum/components/UserBadgesPage', UserBadgesPage);